import { Preferences } from '@capacitor/preferences';
import { isNative } from './platform';

/**
 * Small key/value store with a synchronous read cache.
 * Web: localStorage. Native: Capacitor Preferences (survives WebView storage eviction),
 * mirrored into memory at startup so reads stay synchronous.
 */
const cache = new Map<string, string>();
const PREFIX = 'hud.';

export async function initStorage() {
  if (isNative()) {
    const { keys } = await Preferences.keys();
    await Promise.all(
      keys
        .filter((k) => k.startsWith(PREFIX))
        .map(async (k) => {
          const { value } = await Preferences.get({ key: k });
          if (value != null) cache.set(k, value);
        }),
    );
    return;
  }
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) cache.set(k, localStorage.getItem(k) ?? '');
    }
  } catch {
    /* storage blocked (private mode): run memory-only */
  }
}

export const storage = {
  get(key: string): string | null {
    return cache.get(PREFIX + key) ?? null;
  },
  set(key: string, value: string | null) {
    const k = PREFIX + key;
    if (value === null) cache.delete(k);
    else cache.set(k, value);
    if (isNative()) {
      void (value === null ? Preferences.remove({ key: k }) : Preferences.set({ key: k, value }));
      return;
    }
    try {
      if (value === null) localStorage.removeItem(k);
      else localStorage.setItem(k, value);
    } catch {
      /* ignore */
    }
  },
};
